import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Select from 'components/inputs/select';
import Text from 'components/inputs/text';
import { OrchestratorModelType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { confirmationModal, errorModal, successModal } from 'utils/ui';

const EMPTY_MODEL: Omit<OrchestratorModelType, 'id'> = {
  code: '',
  label: '',
  providerId: 0,
  providerModelCode: ''
};

export default function OrchestratorModelsPage() {
  const dispatch = useAppDispatch();
  const { orchestratorModels, orchestratorProviders } = useAppSelector(
    (state) => state.admin
  );

  const [view, setView] = useState<'table' | 'detail'>('table');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('');

  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [providerId, setProviderId] = useState<number>(0);
  const [providerModelCode, setProviderModelCode] = useState('');

  const selectedModel =
    orchestratorModels.find((m) => m.id === selectedId) ?? null;

  const filteredModels = useMemo(() => {
    let models = orchestratorModels;
    if (providerFilter) {
      models = models.filter((m) => m.providerId === Number(providerFilter));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      models = models.filter(
        (m) =>
          m.code.toLowerCase().includes(q) ||
          m.label.toLowerCase().includes(q) ||
          m.providerModelCode.toLowerCase().includes(q)
      );
    }
    return models;
  }, [orchestratorModels, providerFilter, searchQuery]);

  const loadModel = (model: OrchestratorModelType | null) => {
    if (model) {
      setCode(model.code);
      setLabel(model.label);
      setProviderId(model.providerId);
      setProviderModelCode(model.providerModelCode);
    } else {
      setCode(EMPTY_MODEL.code);
      setLabel(EMPTY_MODEL.label);
      setProviderId(orchestratorProviders[0]?.id ?? 0);
      setProviderModelCode(EMPTY_MODEL.providerModelCode);
    }
  };

  useEffect(() => {
    if (isCreating) {
      loadModel(null);
    } else {
      loadModel(selectedModel);
    }
  }, [selectedId, isCreating, orchestratorModels]);

  const handleSelect = (id: number) => {
    setIsCreating(false);
    setSelectedId(id);
    setView('detail');
  };

  const handleNew = () => {
    setSelectedId(null);
    setIsCreating(true);
    loadModel(null);
    setView('detail');
  };

  const handleBack = () => {
    setView('table');
    setSelectedId(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (
      !code.trim() ||
      !label.trim() ||
      !providerId ||
      !providerModelCode.trim()
    ) {
      errorModal('All fields are required.');
      return;
    }

    const payload = {
      code: code.trim(),
      label: label.trim(),
      providerId,
      providerModelCode: providerModelCode.trim()
    };

    try {
      if (isCreating) {
        await axios.post('orchestrator-model', payload);
        dispatch(fetchDefaultAdminData());
        successModal('Model created.');
      } else if (selectedId) {
        await axios.patch('orchestrator-model', { id: selectedId, ...payload });
        dispatch(fetchDefaultAdminData());
        successModal('Model updated.');
      }
      setIsCreating(false);
      setSelectedId(null);
      setView('table');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    confirmationModal(
      'Are you sure you want to reset? All unsaved changes will be lost.',
      () => {
        if (isCreating) loadModel(null);
        else loadModel(selectedModel);
      }
    );
  };

  const handleDelete = () => {
    if (!selectedId) return;
    confirmationModal(
      `Are you sure you want to delete "${selectedModel?.label}"? This cannot be undone.`,
      async () => {
        try {
          await axios.delete(`orchestrator-model/${selectedId}`);
          setSelectedId(null);
          setView('table');
          dispatch(fetchDefaultAdminData());
          successModal('Model deleted.');
        } catch {
          errorModal('Something went wrong.');
        }
      }
    );
  };

  const getProviderLabel = (pid: number) =>
    orchestratorProviders.find((p) => p.id === pid)?.label ?? '—';

  if (view === 'detail') {
    return (
      <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
        <div className='flex items-center gap-3'>
          <button
            type='button'
            onClick={handleBack}
            className='flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700'
          >
            <ArrowLeftIcon className='h-4 w-4' />
            Back to Models
          </button>
        </div>

        <div className='text-2xl font-bold text-gray-900'>
          {isCreating ? 'New Model' : selectedModel?.label ?? 'Edit Model'}
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>Code</label>
            <Text
              value={code}
              onChange={(v) => setCode(v)}
              placeholder='e.g. gpt-4o'
            />
          </div>

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>Label</label>
            <Text
              value={label}
              onChange={(v) => setLabel(v)}
              placeholder='Display label'
            />
          </div>

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Provider
            </label>
            <Select
              value={providerId}
              onChange={(v) => setProviderId(Number(v))}
              options={orchestratorProviders.map((p) => ({
                label: p.label,
                value: p.id
              }))}
              preventDeselection
            />
          </div>

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Provider Model Code
            </label>
            <Text
              value={providerModelCode}
              onChange={(v) => setProviderModelCode(v)}
              placeholder='e.g. gpt-4o-2024-08-06'
            />
          </div>
        </div>

        <div className='flex items-center gap-3 border-t border-gray-200 pt-4'>
          <Button
            text={isCreating ? 'Create Model' : 'Save Changes'}
            color='green'
            onClick={handleSave}
            disabled={
              !code.trim() || !label.trim() || !providerModelCode.trim()
            }
          />
          <Button text='Reset' color='white' onClick={handleReset} />
          {!isCreating && selectedId && (
            <div className='ml-auto'>
              <Button text='Delete Model' color='red' onClick={handleDelete} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>AI Models</div>

      <div className='flex items-end gap-3'>
        <div className='w-72'>
          <Text
            value={searchQuery}
            onChange={(v) => setSearchQuery(v)}
            placeholder='Search models...'
            controlled
          />
        </div>
        <div className='w-56'>
          <Select
            value={providerFilter}
            onChange={(v) => setProviderFilter(v.toString())}
            options={[
              { label: 'All Providers', value: '' },
              ...orchestratorProviders.map((p) => ({
                label: p.label,
                value: p.id
              }))
            ]}
          />
        </div>
        <div className='ml-auto'>
          <Button
            text='New Model'
            color='green'
            onClick={handleNew}
            icon={PlusIcon}
          />
        </div>
      </div>

      <div className='max-h-[65vh] overflow-auto'>
        <table className='min-w-full border-separate border-spacing-0 divide-y divide-gray-300'>
          <thead>
            <tr>
              {['Code', 'Label', 'Provider', 'Provider Model Code'].map(
                (header) => (
                  <th
                    key={header}
                    scope='col'
                    className='sticky top-0 z-10 border-b border-gray-300 bg-gray-100 px-3 pb-3.5 pt-3.5 text-left text-sm font-semibold text-gray-900'
                  >
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {filteredModels.map((model) => (
              <tr
                key={model.id}
                onClick={() => handleSelect(model.id)}
                className='cursor-pointer hover:bg-gray-50'
              >
                <td className='whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900'>
                  {model.code}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {model.label}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {getProviderLabel(model.providerId)}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {model.providerModelCode}
                </td>
              </tr>
            ))}
            {filteredModels.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className='py-8 text-center text-sm text-gray-400'
                >
                  No models found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='text-sm text-gray-400'>
        {filteredModels.length} model{filteredModels.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
